import appInfo from "@common/appInfo";
import { type DayOfWeek, type GenericMap, type TimeUnit } from "@common/declarations";
import * as dfns from "date-fns";
import { capitalize, padStart, toPairs } from "lodash";
import moment, { type Moment } from "moment";
import "moment-duration-format";
import momentTz from "moment-timezone";

export interface TimeParts {
  seconds: number;
  minutes: number;
  hours: number;
}

export type DateTimeParts = TimeParts & {
  days: number;
  months: number;
  years: number;
};

const isNoTime = (str: string) => /^\d{4}-\d{2}-\d{2}$/.test(str);

const _formatDate = (format: string, value: Date | string | undefined | null, timeZone?: string): string | null => {
  if (!value) return null;

  if (moment(value).isValid() || value instanceof Date) {
    if (timeZone) {
      return `${momentTz.tz(value, timeZone).format(format)}`;
    }
    // IMPORTANT: this handles timezones. e.g. 2021-12-01 will be parsed to the correct date. If we use utc() it may become 2021-11-30
    // TODO: consider handling dates w/o time separately.
    return typeof value === "string" && isNoTime(value)
      ? moment(value).format(format)
      : moment(value).utc().format(format);
  }
  return value;
};

export const DAY_TO_DAY_OF_WEEK: GenericMap<DayOfWeek> = {
  1: "Sunday",
  2: "Monday",
  3: "Tuesday",
  4: "Wednesday",
  5: "Thursday",
  6: "Friday",
  7: "Saturday"
};

export type DateRange = {
  from: Date | string;
  to: Date | string;
};

export type DateRangeWithUnit = {
  range: DateRange | null;
  unit: DateRangeUnit;
};

export type DateRangeUnit =
  | "allTime"
  | "today"
  | "yesterday"
  | "thisWeek"
  | "last7Days"
  | "thisMonth"
  | "thisYear"
  | "last30Days"
  | "custom";

const getUtcDate = (date?: Date): Date => {
  if (!date) date = new Date();
  // utc time - local time, ex. if your time zone is GMT+2, -120 will be returned.
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
};

const utcDateToLocal = (date: Date): Date => {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - userTimezoneOffset);
};

const getDayRange = (duration: number, unit?: "day" | "week" | "month" | "year", from?: Date): DateRange => {
  let endDate;
  switch (unit) {
    case "week":
      endDate = dfns.startOfWeek(dfns.addDays(from ?? new Date(), duration), { weekStartsOn: 1 });
      break;
    case "month":
      endDate = dfns.startOfMonth(dfns.addDays(from ?? new Date(), duration));
      break;
    case "year":
      endDate = dfns.startOfYear(dfns.addDays(from ?? new Date(), duration));
      break;
    default:
      endDate = dfns.startOfDay(dfns.addDays(from ?? new Date(), duration));
      break;
  }

  return {
    from: utcDateToLocal(endDate),
    to: utcDateToLocal(dfns.endOfDay(from ?? new Date()))
  };
};

const unitToMomentUnit = (unit: TimeUnit): moment.unitOfTime.DurationConstructor => {
  switch (unit) {
    case "Day":
      return "d";
    case "Quarter":
      return "Q";
    case "Hour":
      return "h";
    case "Minute":
      return "m";
    case "Month":
      return "M";
    case "Second":
      return "s";
    case "Week":
      return "w";
    case "Year":
      return "y";
  }
};

const getDate = (date: Date | string | number): Date | number => (typeof date === "string" ? new Date(date) : date);

const DATE_FORMAT_SENTENCE = "DD MMMM YYYY";
const DATE_DAY_FORMAT = "DD";
const DATE_DAY_NAME_FORMAT = "dddd";
const DATE_MONTH_DAY_FORMAT = "MMM DD";
const DATE_LONG_MONTH_DAY_FORMAT = "MMMM Do";
const DATE_MONTH_NAME_FORMAT = "MMMM";
const DATE_YEAR_MONTH_FORMAT = "YYYY MMM";
const DATE_YEAR_FORMAT = "YYYY";
const DATE_FORMAT = "YYYY MMM DD";
const DATE_TIME_FORMAT = "YYYY MMM DD, HH:mm";
const TIME_FORMAT = "HH:mm:ss";
const TIME_FORMAT_NO_SECONDS = "HH:mm";
export default {
  DATE_FORMAT,
  DATE_DAY_FORMAT,
  DATE_DAY_NAME_FORMAT,
  DATE_LONG_MONTH_DAY_FORMAT,
  DATE_MONTH_DAY_FORMAT,
  DATE_MONTH_NAME_FORMAT,
  DATE_YEAR_MONTH_FORMAT,
  DATE_YEAR_FORMAT,
  DATE_TIME_FORMAT,
  format: (value: Date | string | undefined | null, format: string): string | null => _formatDate(format, value),
  formatLocal: (value: Date | string | undefined | null, format: string): string | null =>
    _formatDate(format, value, appInfo.business.timeZoneIana),
  formatDate: (value: Date | string | undefined | null): string | null => _formatDate(DATE_FORMAT, value),
  formatDateLocal: (value: Date | string | undefined | null): string | null =>
    _formatDate(DATE_FORMAT, value, appInfo.business.timeZoneIana),
  formatDateTime: (value: Date | string | undefined | null): string | null => _formatDate(DATE_TIME_FORMAT, value),
  formatDateSentence: (value: Date | string | undefined | null): string | null =>
    _formatDate(DATE_FORMAT_SENTENCE, value),
  formatDateTimeLocal: (value: Date | string | undefined | null): string | null =>
    _formatDate(DATE_TIME_FORMAT, value, appInfo.business.timeZoneIana),
  formatDuration: (value: number, unit: any, oneBehaviour: "hide" | "show") => {
    const res = value === 1 ? `${oneBehaviour === "show" ? "1 " : ""}${unit}` : `${value} ${unit}s`;
    return res === unit ? res : res.toLowerCase();
  },
  formatDurationFromNow: (date: string | Moment, capital: boolean) => {
    const v = moment(date).fromNow();
    return capital ? capitalize(v) : v;
  },
  formatRange: (from: Date | string, to: Date | string, opts?: Intl.DateTimeFormatOptions): string => {
    return new Intl.DateTimeFormat("en", opts)["formatRange"](new Date(from), new Date(to));
  },
  formatTime: (value: Date | string | undefined | null, showSeconds: boolean = true): string | null =>
    _formatDate(showSeconds ? TIME_FORMAT : TIME_FORMAT_NO_SECONDS, value),
  fromSecondsToTime: (seconds: number) =>
    moment.utc(moment.duration(seconds, "seconds").asMilliseconds()).format(TIME_FORMAT),
  extractDate: (value: Date | string): string => moment(value).format(moment.HTML5_FMT.DATE),
  getTimeFromSeconds: (totalSeconds: number): DateTimeParts => {
    let parts: DateTimeParts = {
      seconds: 0,
      minutes: 0,
      hours: 0,
      days: 0,
      months: 0,
      years: 0
    };

    parts.seconds = totalSeconds % 60;
    let totalMinutes = (totalSeconds - parts.seconds) / 60;
    parts.minutes = totalMinutes % 60;
    let totalHours = (totalMinutes - parts.minutes) / 60;
    parts.hours = totalHours % 24;
    let totalDays = (totalHours - parts.hours) / 365;
    parts.days = totalDays % 365;
    parts.years = totalDays - parts.days / 365;

    return parts;
  },
  getSecondsFromTime: (timeParts: DateTimeParts) => {
    return (
      (timeParts.seconds || 0) +
      (timeParts.minutes || 0) * 60 +
      (timeParts.hours || 0) * 3600 +
      (timeParts.days || 0) * 3600 * 24 +
      (timeParts.years || 0) * 3600 * 24 * 365
    );
  },
  getUtcDate: (date?: Date): Date => getUtcDate(date),
  getCurrentLocalDate: (): Date => new Date(moment.tz(appInfo.business.timeZoneIana).format("YYYY-MM-DDTHH:mm:ss")),
  formatTimeParts: (hours: number, minutes: number, seconds: number): string =>
    `${padStart(hours.toString(), 2, "0")}:${padStart(minutes.toString(), 2, "0")}:${padStart(
      seconds.toString(),
      2,
      "0"
    )}`,
  parseTime: (time: string): TimeParts | null => {
    const m = time.match(/^(\d{1,2})\:(\d{1,2})\:(\d{1,2})$/);
    if (!m) return null;
    return {
      hours: +m[1],
      minutes: +m[2],
      seconds: +m[3]
    };
  },
  getDayOfWeek: (dayOfWeek: number): DayOfWeek => DAY_TO_DAY_OF_WEEK[dayOfWeek],
  getWeekDayNumber: (dayOfWeek: DayOfWeek): number => +toPairs(DAY_TO_DAY_OF_WEEK).find((x) => x[1] === dayOfWeek)![0],
  get today() {
    return dfns.startOfToday();
  },
  get tomorrow() {
    return dfns.startOfTomorrow();
  },
  isCurrentMonth: (date: string | Date): boolean => {
    const fullDate = new Date(date);
    const year = fullDate.getFullYear();
    const month = fullDate.getMonth();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    return year === currentYear && month === currentMonth;
  },
  isSameMonth: (a: string | Date, b: string | Date) => {
    return dfns.isSameMonth(getDate(a), getDate(b));
  },
  isSameWeek: (a: string | Date, b: string | Date) => {
    return dfns.isSameWeek(getDate(a), getDate(b), {
      weekStartsOn: 1
    });
  },
  isSameYear: (a: string | Date, b: string | Date) => {
    return dfns.isSameYear(getDate(a), getDate(b));
  },
  isInFuture: (date: string | Date): boolean => moment(date) > moment.utc(),
  isInFutureLocal: (date: string | Date): boolean =>
    moment(date) > moment.tz(moment.now(), appInfo.business.timeZoneIana),
  isGreaterThanDate: (value: string | Date, minValue: string | Date): boolean =>
    new Date(value).valueOf() > new Date(minValue).valueOf(),
  getDateRange: (data: DateRangeWithUnit): DateRange | null => {
    switch (data.unit) {
      case "allTime":
        return null;
      case "today":
        return getDayRange(0);
      case "yesterday":
        return getDayRange(0, "day", dfns.add(new Date(), { days: -1 }));
      case "thisWeek":
        return getDayRange(0, "week");
      case "last7Days":
        return getDayRange(-7);
      case "thisMonth":
        return getDayRange(0, "month");
      case "thisYear":
        return getDayRange(0, "year");
      case "last30Days":
        return getDayRange(-30);
      case "custom":
        return {
          from: data.range!.from,
          to: data.range!.to
        };
      default:
        throw new Error(`BUG. Unexpected unit value '${data.unit}'`);
    }
  },
  timeUnitToNumber: (value: TimeUnit | null): number => {
    switch (value) {
      case "Second":
      case "Minute":
        return 60;
      case "Hour":
        return 24;
      case "Day":
        return 365;
      case "Week":
        return 4;
      case "Month":
        return 12;
      case "Year":
        return 100;
      default:
        return 100;
    }
  },
  differenceInDays: (dateLeft: number | Date | string, dateRight: number | Date | string): number =>
    dfns.differenceInDays(getDate(dateLeft), getDate(dateRight)),
  startOfDay: (date: number | Date | string): Date => utcDateToLocal(dfns.startOfDay(getDate(date))),
  startOfMonth: (date: number | Date | string): Date => utcDateToLocal(dfns.startOfMonth(getDate(date))),
  getDayRange: (duration: number, unit?: "day" | "week" | "month", from?: Date): DateRange =>
    getDayRange(duration, unit, from),
  getDaysInCurrentMonth: () => dfns.getDaysInMonth(new Date()),
  getDaysBetween: (from: number | Date | string, to: number | Date | string): number =>
    dfns.differenceInDays(getDate(to), getDate(from)),
  add: (date: string, duration: number, unit: TimeUnit) =>
    moment(date).add(duration, unitToMomentUnit(unit)).toISOString(),
  addDays: (date: number | Date | string, duration: number) => dfns.addDays(getDate(date), duration),
  addYears: (date: number | Date | string, duration: number) => dfns.addYears(getDate(date), duration),
  addMonth: (date: number | Date | string, duration: number) => dfns.addMonths(getDate(date), duration),
  fromNow: (duration: number, unit: moment.unitOfTime.DurationConstructor) =>
    moment().add(duration, unit).fromNow(true),
  fromNowLocal: (mixed: number | string, unit: moment.unitOfTime.DurationConstructor) =>
    typeof mixed === "number"
      ? moment.tz(moment.now(), appInfo.business.timeZoneIana).add(mixed, unit).fromNow(true)
      : moment.tz(mixed, appInfo.business.timeZoneIana).fromNow(true),
  currentDayOfMonth: (): number => new Date().getDate(),
  eachMonthOfInterval: (start: Date | number | string, end: Date | number | string): Date[] =>
    dfns
      .eachMonthOfInterval({
        start: getDate(start),
        end: getDate(end)
      })
      .map((x) => {
        const offset = x.getTimezoneOffset() * 60000;
        return new Date(x.getTime() - offset);
      })
};
